import { setProductSyncRowTourId } from "@bcc/components/tour/tour-ids";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@bcc/components/ui/accordion";
import { Badge } from "@bcc/components/ui/badge";
import { findShipToInfoForCountry } from "@bcc/lib/products/ship-to-countries";
import { useVendorContext } from "@client-shared/providers/vendor";
import { AlertTriangle, Check } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Variant, VendorCountryCode } from "@shared/types";
import type { ConnectedVariantsInfo, GetRemoteFormVariantData, ProductSyncOption } from "./product-sync-modal.types";
import { getOptionFromValues, sanitizeAeroPressGrind } from "./product-sync-modal.connection.utils";
import {
  getRemoteVariantMultiSelectIdentifier,
  setAccordionTriggerBagSizeTitleTestId,
  setAccordionTriggerTestId,
  setAccordionTriggerWebStatusTestId,
  setRowLabelTitleTestId,
  setRowMappingInfoTestId,
  setSelectVariantTestId,
} from "./product-sync-modal.utils";
import { MultiSelect } from "@bcc/components/ui/multi-select";
import { VariantRowPlaceholder } from "./variant-row-placeholder";

export interface BagSizeRowProps {
  bagSize: string;
  localVariant: Variant;
  grinds: ProductSyncOption[];
  onVariantSelect: (sourceObjectId: string) => (targetVariantId: string) => (grindType: string[]) => void;
  options: ProductSyncOption[];
  noAvailablePublishedProducts: string;
  handleGetRemoteFormVariant: (targetVariantId: string) => GetRemoteFormVariantData | undefined;
  connectedVariantsInfo: ConnectedVariantsInfo[];
  handleClear: (targetVariantIds: string[]) => void;
}

export function BagSizeRow({
  onVariantSelect,
  localVariant,
  options,
  bagSize,
  noAvailablePublishedProducts,
  grinds,
  connectedVariantsInfo,
  handleGetRemoteFormVariant,
  handleClear,
}: BagSizeRowProps) {
  const { t } = useTranslation();
  const [{ currentVendor }] = useVendorContext();
  const [targetVariantIds, setTargetVariantIds] = useState<string[]>([]);
  const [mappedOptions, setMappedOptions] = useState<ProductSyncOption[]>([]);
  const [selectedBagSizeGrinds, setSelectedBagSizeGrinds] = useState<Record<string, string[]>>({});
  const [validatedOptions, setValidatedOptions] = useState<ProductSyncOption[]>([]);

  const validateAndEnhanceOptions = useCallback(
    (availableOptions: ProductSyncOption[], selectedGrinds: Set<string>): ProductSyncOption[] => {
      const selectedVariants = availableOptions.filter((variant) => variant.isDisabled).map((variant) => variant.value);
      const filteredOptions = availableOptions
        .filter((option) => {
          const remoteVariant = handleGetRemoteFormVariant(option.value)?.remoteFormVariant;
          return !remoteVariant?.hasDetectedBagSize || remoteVariant.bagSize?.toLowerCase() === bagSize.toLowerCase();
        })
        .map((option) => ({ ...option }));

      if (selectedGrinds.size > 0) {
        return filteredOptions.map((option) => {
          if (selectedVariants.includes(option.value)) {
            return option;
          }
          const remoteVariant = handleGetRemoteFormVariant(option.value)?.remoteFormVariant;
          if (!remoteVariant?.hasValidDetectedGrindType) {
            return option;
          }
          const detectedGrinds = remoteVariant.detectedGrindInfo?.map(
            (detectedGrind) => detectedGrind.mapDetectedGrindTo ?? detectedGrind.grind
          );
          if (!detectedGrinds || detectedGrinds.length === 0) {
            return option;
          }
          const isDisabled = detectedGrinds.some((grind) => selectedGrinds.has(sanitizeAeroPressGrind(grind)));
          return { ...option, isDisabled };
        });
      }
      return filteredOptions;
    },
    [bagSize, handleGetRemoteFormVariant]
  );

  const selectedGrindSet = useMemo(() => {
    const selectedGrinds = Object.values(selectedBagSizeGrinds).flat();
    return new Set(selectedGrinds);
  }, [selectedBagSizeGrinds]);

  useEffect(() => {
    const nextValidated = validateAndEnhanceOptions(options, selectedGrindSet);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-validate options when source options or selected grinds change
    setValidatedOptions(nextValidated);
  }, [options, selectedGrindSet, validateAndEnhanceOptions]);

  const isProductHasMappings = useMemo(() => {
    return connectedVariantsInfo.length > 0;
  }, [connectedVariantsInfo.length]);

  const rowScopedOptions = useMemo(() => {
    // Keep imported disables intact, but let the owning row remove variants
    // that are only disabled because another row cannot also reserve them.
    if (isProductHasMappings || targetVariantIds.length === 0) {
      return validatedOptions;
    }

    return validatedOptions.map((option) => {
      if (!targetVariantIds.includes(option.value)) {
        return option;
      }

      return {
        ...option,
        isDisabled: Boolean(option.isImportedVariant),
      };
    });
  }, [isProductHasMappings, targetVariantIds, validatedOptions]);

  const targetVariantOptions = useMemo(() => {
    return getOptionFromValues(targetVariantIds, isProductHasMappings ? mappedOptions : rowScopedOptions);
  }, [targetVariantIds, isProductHasMappings, mappedOptions, rowScopedOptions]);

  const webStatusData = useMemo(() => {
    return setWebStatusData(localVariant, t, currentVendor?.countryCode);
  }, [currentVendor?.countryCode, localVariant, t]);

  useEffect(() => {
    if (connectedVariantsInfo.length > 0) {
      const nextMappedOptions = connectedVariantsInfo.map((connectedVariant) => {
        return {
          label: connectedVariant.title,
          value: connectedVariant.id,
        } satisfies ProductSyncOption;
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync mapped variant options when connected variants load
      setTargetVariantIds(nextMappedOptions.map((o) => o.value));
      setMappedOptions(nextMappedOptions);
    }
  }, [connectedVariantsInfo]);

  const removeDeselectedGrinds = (deselectedVariants: string[]) => {
    setSelectedBagSizeGrinds((prev) => {
      const updated = { ...prev };
      deselectedVariants.forEach((variantId) => {
        delete updated[variantId];
      });
      return updated;
    });
  };

  const handleSetTargetVariantIds = (values: string[]) => {
    if (!values || values.length === 0) {
      if (targetVariantIds.length > 0) {
        handleClear(targetVariantIds);
      }
      setSelectedBagSizeGrinds({});
      setTargetVariantIds([]);
      return;
    }

    const newValues = getOptionFromValues(values, options);
    const newlySelectedVariants = newValues.filter((newSelected) => {
      return !targetVariantIds.includes(newSelected.value);
    });

    newlySelectedVariants.forEach((newlySelectedVariant) => {
      onVariantSelect(localVariant.id)(newlySelectedVariant.value)([]);
    });

    const deselectedIds = targetVariantIds.filter((id) => !values.includes(id));

    if (deselectedIds.length > 0) {
      handleClear(deselectedIds);
      removeDeselectedGrinds(deselectedIds);
    }

    setTargetVariantIds(values);
  };

  const handleSelectedGrinds = useCallback((variantId: string, grinds: string[]) => {
    setSelectedBagSizeGrinds((prev) => {
      return { ...prev, [variantId]: grinds };
    });
  }, []);

  const handleGrindChange = useCallback(
    (targetVariantId: string) => (grindValues: string[]) => {
      onVariantSelect(localVariant.id)(targetVariantId)(grindValues);
      handleSelectedGrinds(targetVariantId, grindValues);
    },
    [onVariantSelect, localVariant.id, handleSelectedGrinds]
  );

  return (
    <div className="rounded-lg border bg-background p-3 shadow-sm">
      <AccordionItem
        value={`${localVariant.id}-${bagSize}`}
        className="border-b-0"
        testId={setRowLabelTitleTestId(localVariant.id, bagSize)}
        data-tour-id={setProductSyncRowTourId(isProductHasMappings ? "mapped" : "unmapped")}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AccordionTrigger testId={setAccordionTriggerTestId(localVariant.id, bagSize)} className="py-2 hover:no-underline">
            <div className="flex items-center gap-2">
              <span data-testid={setAccordionTriggerBagSizeTitleTestId(localVariant.id, bagSize)} className="font-semibold">
                {bagSize}
              </span>
              {webStatusData?.label && (
                <Badge
                  variant="outline"
                  className={webStatusData.className}
                  testId={setAccordionTriggerWebStatusTestId(localVariant.id, bagSize)}
                >
                  {webStatusData.label}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <Badge
            variant="secondary"
            className={
              isProductHasMappings ? "bg-green-100 text-green-900 hover:bg-green-100" : "bg-blue-100 text-blue-900 hover:bg-blue-100"
            }
            testId={setRowMappingInfoTestId(localVariant.id, bagSize)}
          >
            {isProductHasMappings ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {isProductHasMappings ? connectedVariantsInfo.length : targetVariantIds.length}
            <span className="ml-1">
              {t("components.ProductSyncModal.mapped", {
                defaultValue: "mapped",
              })}
            </span>
          </Badge>
        </div>

        <AccordionContent>
          <div className="space-y-3 px-1">
            <MultiSelect
              identifier={getRemoteVariantMultiSelectIdentifier(localVariant.id, bagSize)}
              disabled={isProductHasMappings}
              value={targetVariantIds}
              options={isProductHasMappings ? mappedOptions : rowScopedOptions}
              onChange={handleSetTargetVariantIds}
              placeholder={t("components.ProductSyncModal.selectPlaceHolder", {
                defaultValue: "Select remote variants",
              })}
              emptyMessage={noAvailablePublishedProducts}
            />
            <div data-testid={setSelectVariantTestId(localVariant.id, bagSize)} />
            <VariantRowPlaceholder
              grinds={grinds}
              values={targetVariantOptions}
              onChange={handleGrindChange}
              getRemoteFormVariant={handleGetRemoteFormVariant}
              connectedVariantsInfo={connectedVariantsInfo}
              isGrindMappingDisabled={isProductHasMappings}
              selectedGrindsByBagSize={selectedGrindSet}
              bagSize={bagSize}
              localVariantId={localVariant.id}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}

const setPillClasses = (webStatus: Variant["webStatus"] | undefined) => {
  switch (webStatus) {
    case "Visible for Sale":
      return "bg-green-100 text-green-900";
    case "Not Visible":
      return "bg-orange-100 text-orange-900";
    case "Out of Stock":
      return "bg-yellow-100 text-yellow-900";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const WEB_STATUS_TRANSLATION_KEYS: Record<NonNullable<Variant["webStatus"]>, string> = {
  "Visible for Sale": "published",
  "Not Visible": "notVisible",
  "Out of Stock": "outOfStock",
  "Temporarily Out of Stock": "outOfStock",
  "Visible but not for Sale": "outOfStock",
};

/**
 * Resolves the status shown on a bag-size row.
 *
 * Cross-border SKUs carry no top-level `webStatus` — their real status lives per
 * ship-to channel. Resolve the signed-in vendor's destination so the modal and
 * products table show the same status when different countries have different
 * availability. Eligibility remains independent and may consider every channel.
 */
const resolveRowWebStatus = (localVariant: Variant, countryCode: VendorCountryCode | undefined): Variant["webStatus"] =>
  findShipToInfoForCountry(localVariant.shipToInfo, countryCode)?.webStatus ?? localVariant.webStatus;

/**
 * "Visible but not for Sale" is the canonical PIM status for a SKU that is on
 * the storefront but cannot be bought, and "Temporarily Out of Stock" is a SKU
 * that is not currently purchasable. Roasters are shown plain "Out of Stock"
 * for both — matching the products table — so states a roaster cannot act on
 * differently are not presented as if they were different, and neither is ever
 * shown as available.
 */
const toDisplayWebStatus = (webStatus: NonNullable<Variant["webStatus"]>): NonNullable<Variant["webStatus"]> =>
  webStatus === "Visible but not for Sale" || webStatus === "Temporarily Out of Stock" ? "Out of Stock" : webStatus;

export const setWebStatusData = (localVariant: Variant, t: TFunction, countryCode?: VendorCountryCode) => {
  const resolved = resolveRowWebStatus(localVariant, countryCode);
  if (!resolved) {
    return undefined;
  }
  const webStatus = toDisplayWebStatus(resolved);
  return {
    label: String(
      t(`views.productsTable.status.${WEB_STATUS_TRANSLATION_KEYS[webStatus]}`, {
        defaultValue: webStatus,
      })
    ),
    className: setPillClasses(webStatus),
  };
};
